import {
  download,
  EllipsedText,
  FlexRow,
  getFileNameFromS3Url,
  IMAGE_EXTENSIONS,
  MaterialIcon,
  StyledButton,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { FileUploader } from 'baseui/file-uploader';
import { ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { getFileUrl } from '../../services/appService';

type Props = {
  imageS3Url?: string;
  file?: File;
  onFileChange: (file?: File) => void;
  onBlur?: () => void;
  hasError?: boolean;
  downloadable?: boolean;
};

export function ImageUploader({
  imageS3Url = '',
  file,
  onFileChange,
  onBlur,
  hasError,
  downloadable = false,
}: Readonly<Props>) {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const [imageErrors, setImageErrors] = useState('');

  const imageObject = file ? URL.createObjectURL(file) : undefined;

  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    const loadImage = async () => {
      if (imageS3Url) {
        const { url } = await getFileUrl(imageS3Url);
        if (url) setImageUrl(url);
        return;
      }
      setImageUrl(undefined);
    };

    loadImage();
  }, [imageS3Url]);

  if (imageUrl || file) {
    return (
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={concatenateClasses(classes.customField, css({ boxSizing: 'border-box', alignItems: 'center' }))}
      >
        <img
          src={imageUrl ?? imageObject}
          alt=""
          className={css({
            height: '25px',
            objectFit: 'cover',
          })}
        />
        <EllipsedText
          breakAll
          inline
          style={{
            fontSize: theme.sizing.scale550,
          }}
        >
          {imageS3Url ? getFileNameFromS3Url(imageS3Url) ?? '' : file?.name ?? ''}
        </EllipsedText>
        <FlexRow
          classNames={css({ marginLeft: 'auto' })}
          gap={theme.sizing.scale0}
          centered
        >
          {downloadable && imageUrl && (
            <Button
              size="mini"
              shape="circle"
              kind="tertiary"
              onClick={() => {
                const filename = getFileNameFromS3Url(imageUrl);
                if (filename) {
                  download(imageUrl, filename);
                }
              }}
            >
              <MaterialIcon
                name="download"
                size={theme.sizing.scale650}
              />
            </Button>
          )}
          <Button
            size="mini"
            shape="circle"
            kind="tertiary"
            onClick={() => {
              onFileChange(undefined);
            }}
          >
            <MaterialIcon
              name="delete"
              size={theme.sizing.scale650}
            />
          </Button>
        </FlexRow>
      </FlexRow>
    );
  }

  return (
    <FileUploader
      accept={IMAGE_EXTENSIONS}
      onDrop={(acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length === 1) {
          setImageErrors('The image is not valid, please upload a JPG, JPEG or PNG image.');
        } else if (rejectedFiles.length > 1) {
          setImageErrors('You can only upload one image at a time.');
        } else {
          setImageErrors('');
        }
        if (acceptedFiles.length === 1) {
          onFileChange(acceptedFiles[0]);
        }
      }}
      errorMessage={imageErrors}
      onBlur={onBlur}
      onRetry={() => {
        setImageErrors('');
      }}
      multiple={false}
      overrides={{
        ErrorMessage: {
          style: {
            maxWidth: '250px',
            textAlign: 'center',
            fontSize: theme.sizing.scale550,
          },
        },
        FileDragAndDrop: {
          style: {
            paddingTop: theme.sizing.scale600,
            paddingBottom: theme.sizing.scale600,
            ...(hasError
              ? {
                  borderColor: theme.colors.inputBorderError,
                  backgroundColor: theme.colors.inputFillError,
                }
              : {}),
          },
        },
        ContentMessage: {
          component: () => (
            <ParagraphSmall
              marginTop={0}
              marginBottom={theme.sizing.scale300}
              color={theme.colors.contentSecondary}
            >
              Drag an image here or...
            </ParagraphSmall>
          ),
        },
        RetryButtonComponent: {
          component: ({ onClick }) => (
            <StyledButton
              shape="pill"
              size="mini"
              onClick={onClick}
              overrides={{
                BaseButton: {
                  style: { marginTop: theme.sizing.scale300 },
                },
              }}
            >
              Accept
            </StyledButton>
          ),
        },
        ButtonComponent: {
          component: ({ ...props }) => (
            <StyledButton
              {...props}
              kind="primary"
              shape="pill"
              size="mini"
            >
              Search image
            </StyledButton>
          ),
        },
      }}
    />
  );
}
